import { Component, Input, Output, EventEmitter, ChangeDetectorRef, OnInit } from '@angular/core';
import { Template, TemplateFolder } from '../pages/generator/email.store';
import { TemplateFolderService } from '../../service/template-folder.service';
import { GeneratorService } from '../../service/generator.service';

@Component({
  selector: 'app-template-modal',
  templateUrl: './template-modal.component.html',
  styleUrls: ['./template-modal.component.scss'],
  standalone: false
})
export class TemplateModalComponent implements OnInit {
  @Input() templates: Template[] = [];
  @Input() inline = false;
  @Output() templateSelected = new EventEmitter();

  isOpen = false;
  searchQuery: string = '';

  folders: TemplateFolder[] = [];
  selectedFolder: TemplateFolder | null = null;
  newFolderName: string = '';
  isCreatingFolder = false;

  showStripoImport = false;
  showStripoDialog = false;
  stripoEmailId = '';
  stripoImportName = '';
  isImporting = false;

  constructor(
    private cdRef: ChangeDetectorRef,
    private folderService: TemplateFolderService,
    private generatorService: GeneratorService
  ) { }

  ngOnInit() {
    if (this.inline) {
      this.loadFolders();
      this.checkStripoImport();
    }
  }

  get filteredTemplates(): Template[] {
    let filtered = this.templates;

    // Filter by Folder
    if (this.selectedFolder) {
      // If it's a "real" folder
      if (this.selectedFolder.id > 0) {
        filtered = filtered.filter(t => t.folder === this.selectedFolder!.id);
      }
    } else {
      // If no folder selected (Root view or All View?)
      // Let's assume Default is "All Templates" for now, or "Uncategorized"?
      // Usually "All templates" is better default.
    }

    if (!this.searchQuery || this.searchQuery.trim() === '') {
      return filtered;
    }

    const query = this.searchQuery.toLowerCase().trim();
    return filtered.filter(template =>
      template.name?.toLowerCase().includes(query)
    );
  }

  selectTemplate(template: Template) {
    this.templateSelected.emit(template);
    this.close();
  }

  open() {
    this.isOpen = true;
    this.searchQuery = '';
    this.loadFolders();
    this.checkStripoImport();
    this.cdRef.detectChanges();
  }

  checkStripoImport() {
    this.generatorService.checkStripoImport().subscribe(available => {
      this.showStripoImport = available;
      this.cdRef.detectChanges();
    });
  }

  openStripoImportDialog() {
    this.stripoEmailId = '';
    this.stripoImportName = '';
    this.showStripoDialog = true;
  }

  cancelStripoImport() {
    this.showStripoDialog = false;
  }

  confirmStripoImport() {
    if (!this.stripoEmailId.trim() || this.isImporting) return;
    this.isImporting = true;
    this.generatorService.importHtmlFromStripoEmailId(
      this.stripoEmailId.trim(),
      this.stripoImportName || undefined,
      this.selectedFolder?.id ?? null
    ).subscribe({
      next: (result) => {
        this.isImporting = false;
        this.showStripoDialog = false;
        this.templateSelected.emit({ id: result.id, name: result.name, type: 'stripo_import' });
        this.close();
      },
      error: () => {
        this.isImporting = false;
      }
    });
  }

  close() {
    this.isOpen = false;
  }

  loadFolders() {
    this.folderService.getFolders().subscribe(folders => {
      this.folders = folders;
      this.cdRef.detectChanges();
    });
  }

  selectFolder(folder: TemplateFolder | null) {
    this.selectedFolder = folder;
  }

  startCreateFolder() {
    this.isCreatingFolder = true;
    this.newFolderName = '';
  }

  createFolder() {
    if (!this.newFolderName.trim()) return;

    this.folderService.createFolder(this.newFolderName, this.selectedFolder?.id).subscribe(newFolder => {
      this.loadFolders();
      this.isCreatingFolder = false;
      this.newFolderName = '';
    });
  }

  cancelCreateFolder() {
    this.isCreatingFolder = false;
    this.newFolderName = '';
  }



  deleteFolder(folder: TemplateFolder, event: Event) {
    event.stopPropagation();
    if (confirm('Are you sure you want to delete this folder?')) { // Simple confirm for now
      this.folderService.deleteFolder(folder.id).subscribe(() => {
        if (this.selectedFolder?.id === folder.id) {
          this.selectedFolder = null;
        }
        this.loadFolders();
      });
    }
  }

  // Placeholder for move logic - functionality to be added to UI
  moveTemplate(template: Template, folder: TemplateFolder | null) {
    const folderId = folder ? folder.id : null;
    this.generatorService.updateTemplate(template.id, { folder: folderId }).subscribe(updated => {
      const index = this.templates.findIndex(t => t.id === updated.id);
      if (index > -1) {
        this.templates[index].folder = updated.folder;
      }
      this.cdRef.detectChanges();
    });
  }

  getFolderName(folderId: number | null | undefined): string {
    if (!folderId) return '';
    const folder = this.folders.find(f => f.id === folderId);
    return folder ? folder.name : '';
  }
}
